/**
 * TD Visible Discount - Web Component
 * ====================================
 * Handles dynamic price updates for the visible discount feature when
 * variants change on PDP. Listens for the theme's `variant:changed` event
 * and recalculates the discounted price based on the stored percentage.
 *
 * Only initialised on PDP where a product form exists. On collection cards
 * the discount display is purely server-rendered (Liquid) and static.
 */

if (!customElements.get('td-visible-discount')) {
  class TdVisibleDiscount extends HTMLElement {
    constructor() {
      super();

      /* Data attributes */
      this.discountPercent = parseFloat(this.getAttribute('data-td-visible-discount-percent')) || 0;

      /* DOM references */
      this.priceEl = this.querySelector('[data-td-vd-price]');
      this.originalPriceEl = this.querySelector('[data-td-vd-original]');
      this.form = null;
      this.labelList = null;
    }

    connectedCallback() {
      if (!this.discountPercent || !this.priceEl) return;

      /* Find the product form to listen for variant changes */
      const productMeta = this.closest('product-meta');
      if (productMeta) {
        const formId = productMeta.getAttribute('form-id');
        if (formId) {
          this.form = document.getElementById(formId);
        }
        /* Reference the label list so we can re-inject the badge after variant change */
        this.labelList = productMeta.querySelector('[data-product-label-list]');
      }

      if (this.form) {
        this._boundVariantChanged = this._onVariantChanged.bind(this);
        this.form.addEventListener('variant:changed', this._boundVariantChanged);
      }
    }

    disconnectedCallback() {
      if (this.form && this._boundVariantChanged) {
        this.form.removeEventListener('variant:changed', this._boundVariantChanged);
      }
    }

    /**
     * Handle variant change event from the theme's product-variants component.
     * Recalculates the discounted price using the new variant's price.
     * @param {CustomEvent} event - variant:changed event with event.detail.variant
     */
    _onVariantChanged(event) {
      const variant = event.detail.variant;
      if (!variant || !variant.price) return;

      /* Update the original price display */
      const originalFormatted = this._formatPrice(variant.price);
      if (this.originalPriceEl) {
        this.originalPriceEl.textContent = originalFormatted;
      }

      /* Update the discounted price display */
      const discountedCents = Math.round(variant.price * (1 - this.discountPercent / 100));
      const discountedFormatted = this._formatPrice(discountedCents);
      if (this.priceEl) {
        this.priceEl.textContent = discountedFormatted;
      }

      /* Re-inject discount badge — the theme's _updateLabels() wipes the label list
         innerHTML on every variant change and only re-adds badges for native
         compare_at_price discounts. We use requestAnimationFrame to ensure this
         runs after all synchronous variant:changed handlers have completed. */
      this._reinjectBadge(variant);
    }

    /**
     * Re-inject the visible-discount badge into the label list after the theme
     * clears it. Uses requestAnimationFrame so it executes after the theme's
     * synchronous _updateLabels handler has finished.
     * @param {Object} variant - The selected variant object
     */
    _reinjectBadge(variant) {
      if (!this.labelList) return;

      requestAnimationFrame(() => {
        /* Don't show badge if variant is sold out (theme shows its own label) */
        if (!variant.available) return;

        /* Remove any existing badges to avoid duplicates. The metaobject discount
           takes priority over native compare_at_price badges, so clear all savings
           badges before adding ours. */
        this.labelList.querySelectorAll('[data-price-savings], [data-td-vd-badge]').forEach(el => el.remove());

        /* Build savings string using the theme's translation string (e.g. "Save @savings@").
           Falls back to English only if themeVariables is unavailable. */
        const savings = this.discountPercent + '%';
        const collectionDiscount = window.themeVariables?.strings?.collectionDiscount;
        const badgeText = collectionDiscount
          ? collectionDiscount.replace('@savings@', savings)
          : 'Save ' + savings;

        const badge = document.createElement('span');
        badge.className = 'label label--highlight';
        badge.setAttribute('data-td-vd-badge', '');
        badge.setAttribute('data-price-savings', '');
        badge.textContent = badgeText;
        this.labelList.appendChild(badge);
      });
    }

    /**
     * Format price in cents using Shopify's money format settings.
     * Uses window.themeVariables which is set by the theme.
     * @param {number} cents - Price in cents
     * @returns {string} Formatted price string
     */
    _formatPrice(cents) {
      const settings = window.themeVariables?.settings;
      if (!settings) return this._basicFormat(cents);

      const formatString = settings.currencyCodeEnabled
        ? settings.moneyWithCurrencyFormat
        : settings.moneyFormat;

      if (!formatString) return this._basicFormat(cents);

      /* Only strip trailing zeros when currency code is disabled, matching
         the Liquid logic: money_without_trailing_zeros vs money_with_currency */
      const stripTrailingZeros = !settings.currencyCodeEnabled;
      return this._formatMoney(cents, formatString, stripTrailingZeros);
    }

    /**
     * Replicates the theme's formatMoney logic for consistent price display.
     * @param {number} cents - Price in cents
     * @param {string} format - Shopify money format string
     * @param {boolean} stripTrailingZeros - Whether to remove trailing .00 / ,00
     * @returns {string} Formatted price
     */
    _formatMoney(cents, format, stripTrailingZeros) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);
      if (!match) return this._basicFormat(cents);

      const formatWithDelimiters = (number, precision, thousands, decimal) => {
        precision = precision == null ? 2 : precision;
        thousands = thousands == null ? ',' : thousands;
        decimal = decimal == null ? '.' : decimal;

        if (isNaN(number) || number == null) return '0';

        number = (number / 100).toFixed(precision);
        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';
        return dollarsAmount + centsAmount;
      };

      let value = '';
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      /* Remove trailing zeros (.00 or ,00) to match money_without_trailing_zeros.
         Only strip when currency code is disabled; money_with_currency keeps them. */
      if (stripTrailingZeros) {
        value = value.replace(/[.,]00$/, '');
      }

      return format.replace(placeholderRegex, value);
    }

    /**
     * Fallback basic price formatter if theme variables are not available.
     * @param {number} cents - Price in cents
     * @returns {string} Basic formatted price
     */
    _basicFormat(cents) {
      return '$' + (cents / 100).toFixed(2).replace('.00', '');
    }
  }

  customElements.define('td-visible-discount', TdVisibleDiscount);
}
